import React, { useEffect, useState } from "react";
import { ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { Link, useNavigate } from "react-router-dom";
import { EyeIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon, PhotoIcon } from "@heroicons/react/24/outline";
import moment from "moment";

const compactSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 2px",
    minHeight: "28px",
    fontSize: "12px",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "28px" }),
  option: (base: any, state: any) => ({
    ...base, backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b", fontSize: "12px", padding: "4px 10px",
  }),
  menu: (base: any) => ({ ...base, borderRadius: "10px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.1)", zIndex: 9999 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "12px" }),
};

const statusColors: Record<string, string> = {
  working: "bg-green-100 text-green-800",
  not_working: "bg-orange-100 text-orange-800",
  destroyed: "bg-red-100 text-red-800",
  sold: "bg-gray-200 text-gray-700",
  disposed: "bg-gray-200 text-gray-700",
};

const categoryOptions = [
  { value: "printer", label: "Printer" },
  { value: "pc", label: "PC" },
  { value: "laptop", label: "Laptop" },
  { value: "vehicle", label: "Vehicle" },
  { value: "furniture", label: "Furniture" },
  { value: "other", label: "Other" },
];

const statusOptions = [
  { value: "working", label: "Working" },
  { value: "not_working", label: "Not Working" },
  { value: "destroyed", label: "Destroyed" },
  { value: "sold", label: "Sold" },
  { value: "disposed", label: "Disposed" },
];

const AssetList = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [category, setCategory] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);
  const [subbranch, setSubbranch] = useState<any>(null);
  const [subbranchOptions, setSubbranchOptions] = useState<any[]>([]);

  useQuery(["subbranchListForAsset"], () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }), {
    onSuccess: (res: any) => {
      if (res?.status === "SUCCESS") {
        setSubbranchOptions((res?.data?.data || res?.data?.docs || []).map((s: any) => ({ value: s.id, label: s.branch_name || s.name || `#${s.id}` })));
      }
    },
  });

  const buildQuery = () => {
    const q: any = { isDeleted: false };
    if (searchText) q.search = searchText;
    if (category?.value) q.category = category.value;
    if (status?.value) q.status = status.value;
    if (subbranch?.value) q.subbranch = subbranch.value;
    return q;
  };

  const { data: assetData, isLoading, isFetching, refetch } = useQuery(
    ["assetList", currentPage],
    () => ListCommanWay("/admin/asset/list", buildQuery(), { page: currentPage, paginate: perPage, sort: { createdAt: -1 } }),
    { enabled: false, keepPreviousData: true }
  );

  const assets = assetData?.status === "SUCCESS" ? assetData?.data?.docs || assetData?.data?.data || assetData?.data?.rows || [] : [];
  const totalPages = assetData?.data?.pages || assetData?.data?.paginator?.pageCount || 1;
  const totalCount = assetData?.data?.total || assetData?.data?.paginator?.itemCount || 0;

  const { mutate: deleteAsset } = useMutation(
    (id: number) => UpdateCommanWay({ url: `/admin/asset/delete/${id}`, data: {} }),
    {
      onSuccess: (res: any) => { if (res?.status === "SUCCESS") { UseToast("Deleted", "success"); refetch(); } else UseToast(res?.message || "Failed", "error"); },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err?.message || "Error", "error"),
    }
  );

  const handleDelete = (id: number) => {
    Swal.fire({ title: "Delete Asset?", text: "Are you sure?", icon: "warning", showCancelButton: true, confirmButtonText: "Delete", confirmButtonColor: "#d33" }).then((r) => { if (r.isConfirmed) deleteAsset(id); });
  };

  useEffect(() => { refetch(); }, [currentPage]);

  const handleSearch = () => { if (currentPage === 1) refetch(); else setCurrentPage(1); };

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1 min-h-screen">
      {/* Header */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              Asset Management
            </h1>
            <Button size="sm" color="primary" variant="solid" className="h-7 text-xs" onPress={() => navigate("/asset/create")} startContent={<PlusIcon className="w-3.5 h-3.5" />}>
              Add Asset
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl px-3 py-2 shadow-xl shadow-slate-200/50 mb-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-40">
            <Select options={categoryOptions} value={category} onChange={(v: any) => setCategory(v)} isClearable placeholder="Category" styles={compactSelectStyles} menuPortalTarget={document.body} />
          </div>
          <div className="w-40">
            <Select options={subbranchOptions} value={subbranch} onChange={(v: any) => setSubbranch(v)} isClearable placeholder="Branch" styles={compactSelectStyles} menuPortalTarget={document.body} />
          </div>
          <div className="w-36">
            <Select options={statusOptions} value={status} onChange={(v: any) => setStatus(v)} isClearable placeholder="Status" styles={compactSelectStyles} menuPortalTarget={document.body} />
          </div>
          <input
            type="text"
            className="px-2 py-[3px] rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20 w-44"
            placeholder="Name / Tag..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button size="sm" color="primary" variant="solid" className="h-7 text-xs" onPress={handleSearch} isLoading={isFetching} startContent={<MagnifyingGlassIcon className="w-3.5 h-3.5" />}>
            Search
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl shadow-slate-200/50 overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50/80">
            <tr>
              {["#", "Asset Tag", "Name", "Model", "Category", "Branch", "Status", "Book Value", "Warranty", "Photos", ""].map((h) => (
                <th key={h} className="px-2 py-1.5 text-left text-[10px] font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr><td colSpan={11} className="py-6 text-center"><Spinner /></td></tr>
            ) : assets.length === 0 ? (
              <tr><td colSpan={11} className="py-6 text-center text-slate-400 text-sm">No assets found</td></tr>
            ) : (
              assets.map((a: any, idx: number) => {
                const wExpired = a.warrantyEndDate && moment(a.warrantyEndDate).isBefore(moment());
                return (
                  <tr key={a.id} className="hover:bg-blue-50/40 transition-colors">
                    <td className="px-2 py-1.5 text-slate-500">{(currentPage - 1) * perPage + idx + 1}</td>
                    <td className="px-2 py-1.5 font-mono font-semibold">
                      <Link to={`/asset/${a.id}`} className="text-blue-600 hover:underline">{a.assetTag || "-"}</Link>
                    </td>
                    <td className="px-2 py-1.5 font-medium text-slate-800">{a.name}</td>
                    <td className="px-2 py-1.5 text-slate-600">{a.modelNo || "-"}</td>
                    <td className="px-2 py-1.5 capitalize text-slate-600">{a.category || "-"}</td>
                    <td className="px-2 py-1.5 text-slate-600">{a._subbranch?.branch_name || "-"}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusColors[a.status] || "bg-gray-100"}`}>
                        {a.status?.replace("_", " ") || "-"}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-medium">₹{a.bookValue?.toLocaleString("en-IN") || "0"}</td>
                    <td className="px-2 py-1.5">
                      {a.warrantyEndDate ? (
                        <span className={wExpired ? "text-red-600 font-semibold" : "text-slate-600"}>
                          {moment(a.warrantyEndDate).format("DD/MM/YY")}{wExpired && " ⚠"}
                        </span>
                      ) : "-"}
                    </td>
                    <td className="px-2 py-1.5">
                      {a.imageLinks ? (
                        <div className="relative group inline-block">
                          <button className="text-blue-500 hover:text-blue-700 flex items-center gap-0.5">
                            <PhotoIcon className="w-3.5 h-3.5" />
                            <span className="text-[10px]">{a.imageLinks.split(",").filter((l: string) => l.trim()).length}</span>
                          </button>
                          <div className="hidden group-hover:block absolute z-[9999] right-0 bottom-full mb-1 bg-white border rounded-lg shadow-lg p-2 min-w-[120px]">
                            {a.imageLinks.split(",").map((url: string, pi: number) => url.trim() && (
                              <a key={pi} href={url.trim()} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-blue-600 hover:underline py-0.5 truncate max-w-[200px]">
                                Image {pi + 1}
                              </a>
                            ))}
                          </div>
                        </div>
                      ) : "-"}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => navigate(`/asset/${a.id}`)} className="text-blue-500 hover:text-blue-700"><EyeIcon className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(a.id)} className="text-red-400 hover:text-red-600"><TrashIcon className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 px-1">
          <span className="text-[11px] text-slate-500">Page {currentPage}/{totalPages} ({totalCount})</span>
          <div className="flex gap-1">
            <Button size="sm" variant="flat" className="h-6 text-[11px]" isDisabled={currentPage <= 1} onPress={() => setCurrentPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="flat" className="h-6 text-[11px]" isDisabled={currentPage >= totalPages} onPress={() => setCurrentPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetList;
